<template>
  <ErrorBoundary name="CurrentPlan">
    <div :class="props.config == 'page' ? ['lg:w-[25rem]', 'mx-auto'] : []">
      <StateMachine
        :components="components"
        :routes="routes"
        :initial-data="initialData"
        client-only
        :id="id"
      />
    </div>
  </ErrorBoundary>
</template>

<script lang="ts" setup>
  import { SessionStore } from '~/stores/session'
  import { ProfileStore } from '~/stores/profile'
  import StateMachine from '~/components/StateMachine.vue'
  import type { IStateMachineRoutes } from '~/models/StateMachineModels'
  import { QuotedPlanVm } from '~/models/quoting/QuotedPlan'
  import ErrorBoundary from '~/components/errors/ErrorBoundary.vue'
  import dayjs from 'dayjs'
  import { EngineStore } from '~/stores/engine'

  export type Config = 'sidebar' | 'page'

  const profile = ProfileStore.use()
  const session = SessionStore.use()
  const engine = EngineStore.use()

  const emit = defineEmits(['close'])

  const props = defineProps<{
    config: Config
    originalQuote: QuotedPlanVm
    id: string
  }>()

  const { originalQuote } = toRefs(props)

  const currentPlan = computed(() =>
    !_isNil(profile.needsAssessment.currentPlan?.[dayjs().year().toString()])
      ? engine.quotesCatalog[profile.needsAssessment.currentPlan![dayjs().year().toString()]!]
      : null
  )

  console.log('CURRENT PLAN SETUP: ', currentPlan.value, engine.quotesCatalog)

  const initialData = computed(() => {
    const data = []

    if (currentPlan.value) {
      data.push({
        key: 'currentPlan',
        value: currentPlan.value
      })
    }

    if (originalQuote.value) {
      data.push({
        key: 'originalQuote',
        value: originalQuote.value
      })
    }

    return data
  })

  const components = {
    compare: defineAsyncComponent(
      () => import('~/components/currentPlan/components/CurrentPlanCompare.vue')
    ),
    carrierSearch: defineAsyncComponent(
      () => import('~/components/currentPlan/components/CurrentPlanCarrierSearch.vue')
    ),
    planSelect: defineAsyncComponent(
      () => import('~/components/currentPlan/components/CurrentPlanSelect.vue')
    )
  }

  // create union type of all component keys
  export type CurrentPlanComponents = keyof typeof components

  export interface CurrentPlanStateMachineRoutes extends IStateMachineRoutes {
    initial: CurrentPlanComponents
    components: Partial<{ [key in CurrentPlanComponents]: { next: CurrentPlanComponents } }>
  }

  const routes = computed(
    () =>
      ({
        initial: 'compare',
        components: {
          compare: { next: 'carrierSearch' },
          carrierSearch: { next: 'planSelect' },
          planSelect: { next: 'compare' }
        }
      } as CurrentPlanStateMachineRoutes)
  )
</script>

<style scoped></style>
